import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Sparkles, ArrowLeft, MapPin, Loader2 } from "lucide-react";
import { BUSINESS_LABEL } from "../data/businesses";

const CITY_COORDS = {
  Acapulco: [16.86, -99.88],
  "Ciudad de México": [19.43, -99.13],
  Guadalajara: [20.66, -103.35],
  Monterrey: [25.67, -100.31],
  Puebla: [19.04, -98.2],
  Mérida: [20.97, -89.62],
};
const DEFAULT_COORDS = [19.43, -99.13];

// Profile giro → DENUE search keyword (accentless for safe URLs).
const GIRO_KEYWORD = {
  taqueria: "taqueria",
  polleria: "polleria",
  gym: "gimnasio",
  ropa: "ropa",
  abarrotes: "abarrotes",
  bar: "bar",
  estetica: "estetica",
  cafeteria: "cafeteria",
  puesto: "comida",
  tortilleria: "tortilleria",
  venta_redes: "tienda",
  otro: "negocio",
};

const DENUE_TOKEN = "ab64ba51-b355-4e06-ab7a-276cbce9d03e";

function buildAddress(n) {
  const parts = [];
  if (n.Calle) parts.push(n.Calle);
  if (n.Num_Exterior) parts.push(n.Num_Exterior);
  let line = parts.join(" ");
  if (n.Colonia) line += (line ? ", " : "") + n.Colonia;
  return line || "Dirección no disponible";
}

export default function CompetenciaMap({ perfil, onBack, onGenerateStrategy }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(null); // null = loading, number = done

  const giroLabel = BUSINESS_LABEL[perfil.giro] || "negocio";

  useEffect(() => {
    const [lat, lon] = CITY_COORDS[perfil.ciudad] || DEFAULT_COORDS;

    // Init map once
    if (!mapRef.current && containerRef.current) {
      mapRef.current = L.map(containerRef.current).setView([lat, lon], 14);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap",
        maxZoom: 19,
      }).addTo(mapRef.current);
    }

    let cancelled = false;
    const keyword = GIRO_KEYWORD[perfil.giro] || "negocio";
    // DENUE requires at least 4 decimal places in the coordinates, otherwise it
    // returns "No hay resultados" — pad to 4 decimals.
    const url = `https://www.inegi.org.mx/app/api/denue/v1/consulta/buscar/${encodeURIComponent(
      keyword,
    )}/${lat.toFixed(4)},${lon.toFixed(4)}/1000/${DENUE_TOKEN}`;

    fetch(url)
      .then((r) => (r.ok ? r.text() : null))
      .then((text) => {
        if (cancelled) return;
        let data = null;
        try {
          data = JSON.parse(text);
        } catch {
          data = null;
        }
        const list = Array.isArray(data) ? data : [];
        list.forEach((n) => {
          const la = parseFloat(n.Latitud);
          const lo = parseFloat(n.Longitud);
          if (Number.isNaN(la) || Number.isNaN(lo) || !mapRef.current) return;
          L.circleMarker([la, lo], {
            radius: 7,
            color: "#dc2626",
            fillColor: "#ef4444",
            fillOpacity: 0.85,
            weight: 2,
          })
            .addTo(mapRef.current)
            .bindPopup(
              `<strong>${n.Nombre || "Negocio"}</strong><br/>${buildAddress(n)}`,
            );
        });
        setCount(list.length);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setCount(0);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [perfil.ciudad, perfil.giro]);

  // Clean up the map on unmount
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const hasCompetitors = count != null && count > 0;

  return (
    <div
      className="flex flex-col min-h-[100dvh] px-6 pt-10 pb-10 animate-fade-in-up"
      data-testid="competencia-screen"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-orange-500 text-white shadow-md shadow-orange-500/30">
            <Sparkles className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <span className="text-lg font-bold tracking-tight text-slate-900">
            Nego<span className="text-orange-500">IA</span>
          </span>
        </div>
        <button
          type="button"
          onClick={onBack}
          data-testid="competencia-back-button"
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 active:text-slate-800"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.4} />
          Volver
        </button>
      </div>

      {/* Title */}
      <div className="mt-6 flex items-start gap-3">
        <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-red-500 text-white shrink-0 shadow-md shadow-red-500/30">
          <MapPin className="w-6 h-6" strokeWidth={2.5} />
        </span>
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 leading-tight">
            🗺️ Mi competencia
          </h1>
          <p className="text-xs text-slate-500 mt-1 leading-snug">
            {giroLabel} cerca de ti en {perfil.ciudad}
          </p>
        </div>
      </div>

      {/* Map */}
      <div className="mt-6 relative rounded-2xl overflow-hidden border-2 border-slate-200">
        <div
          ref={containerRef}
          data-testid="competencia-map"
          style={{ height: "340px", width: "100%" }}
        />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-[500]">
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        )}
      </div>

      {/* Results */}
      {count != null && (
        <div className="mt-5">
          {hasCompetitors ? (
            <>
              <p
                className="text-sm font-semibold text-slate-800 leading-relaxed"
                data-testid="competencia-count"
              >
                Encontramos{" "}
                <span className="text-red-600 font-extrabold">{count}</span> negocios
                similares en 1km a tu alrededor.
              </p>
              <button
                type="button"
                onClick={onGenerateStrategy}
                data-testid="competencia-strategy-button"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 py-4 rounded-xl text-base font-bold bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:bg-orange-600 active:scale-[0.98] transition-all"
              >
                Generar estrategia para destacar →
              </button>
            </>
          ) : (
            <p
              className="text-sm font-semibold text-teal-700 leading-relaxed bg-teal-50 border-2 border-teal-200 rounded-xl p-4"
              data-testid="competencia-empty"
            >
              No encontramos competidores registrados cerca. ¡Eso es una ventaja!
            </p>
          )}
        </div>
      )}
    </div>
  );
}
